import React from 'react';
import styled from 'styled-components';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Trans, useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';

import { getContinueUrl } from 'helpers/appVersions';
import { NAME_REGEX } from 'helpers/yup';
import FormGroup, { RowGroup } from 'components/form-group';
import Button from 'components/button';
import Input from 'components/input';
import Phone from 'components/phone';
import useConfirmDialog from 'hooks/useConfirmDialog';

import {
  CREATE_APPLICATION,
  LIST_APPLICATIONS,
  APPLICATION_PROMOTION_SOURCES
} from '../../dal/applications';
import { UserContext, useNavigateWithContext } from '../../state';
import { Row, Title, PageContainer, PageWrapper } from '../application-form-v2/common-styles';
import ApplicationItem from '../applications/application-item';
import { promoteApp } from '../showing/components/promotion/common';

const Grid = styled.div`
  margin-top: 10px;
`;
const ConfirmWrapper = styled.div`
  overflow: auto;
`;

export default ({ location }) => {
  const { t } = useTranslation();
  const params = useParams();
  const confirmDialog = useConfirmDialog();
  const navigate = useNavigateWithContext();
  const { userContext } = React.useContext(UserContext);
  const initialValues = {
    email: userContext?.email || '',
    phone: [{ number: userContext?.phone || '', type: 'mobile' }],
    firstName: userContext?.firstName || '',
    lastName: userContext?.lastName || '',
    middleName: userContext?.middleName
  };

  const handleContinue = async (values) => {
    if (saveInProgress) {
      return;
    }
    setSaveInProgress(true);

    //Check if app already exists
    try {
      const existingApps = await LIST_APPLICATIONS({ userContext, locationId: location._id });
      if (existingApps?.applications?.length) {
        confirmDialog({
          width: '800px',
          contentStyle: { overflow: 'auto' },
          title: <Trans i18nKey="duplicateApplication.title">Create another application?</Trans>,
          description: (
            <ConfirmWrapper>
              <Trans i18nKey="duplicateApplication.description">
                You already have an application open for this location. Would you like to submit
                another one?
              </Trans>
              <Grid>
                {existingApps?.applications
                  ?.sort((a, b) => {
                    let firstDate = new Date(a?.updatedAt).getTime();
                    let secondDate = new Date(b?.updatedAt).getTime();
                    if (!isNaN(firstDate) && !isNaN(secondDate)) {
                      return secondDate - firstDate;
                    }
                    if (isNaN(firstDate) && isNaN(secondDate)) {
                      return 0;
                    }
                    return isNaN(firstDate) ? -1 : 1;
                  })
                  ?.map((application, ix) => (
                    <ApplicationItem
                      key={ix}
                      application={application}
                      navigate={(...params) => {
                        navigate(...params);
                        confirmDialog({ hide: true });
                      }}
                      hideDelete={true}
                      hideAdd={true}
                    />
                  ))}
              </Grid>
            </ConfirmWrapper>
          ),
          onConfirm: () => performSave(values),
          onCancel: () => {
            setSaveInProgress(false);
          },
          confirmWidth: 150,
          cancelWidth: 100,
          confirmLabel: t('buttonsActions.addAnother'),
          cancelLabel: t('buttonsActions.cancel')
        });
        return;
      }
    } catch (err) {
      console.error('Error checking for existing applications', err);
    }

    //Check if there is a base tour application to apply
    try {
      const existingTourApps = await APPLICATION_PROMOTION_SOURCES({
        userContext,
        companyId: location?.companyId,
        locationId: location._id
      });
      if (existingTourApps?.applications?.length > 0) {
        confirmDialog({
          width: '800px',
          contentStyle: { overflow: 'auto' },
          title: <Trans i18nKey="linkTour.title">Link your tour?</Trans>,
          description: ({ handleCancel }) => {
            const [saving, setSaving] = React.useState(false);
            return (
              <ConfirmWrapper>
                <Trans i18nKey="linkTour.description">
                  You recently toured this location. Would you like to save time and import your
                  data from that tour request?
                </Trans>
                <Grid>
                  {existingTourApps?.applications
                    ?.sort((a, b) => {
                      let firstDate = new Date(a?.submittedAt).getTime();
                      let secondDate = new Date(b?.submittedAt).getTime();
                      if (!isNaN(firstDate) && !isNaN(secondDate)) {
                        return secondDate - firstDate;
                      }
                      if (isNaN(firstDate) && isNaN(secondDate)) {
                        return 0;
                      }
                      return isNaN(firstDate) ? -1 : 1;
                    })
                    ?.map((application, ix) => (
                      <ApplicationItem
                        key={ix}
                        application={application}
                        navigate={() => {
                          if (saving) return;
                          setSaving(true);
                          promoteApp({
                            confirmDialog,
                            userContext,
                            locationId: location._id,
                            showingId: application._id,
                            navigate,
                            onFinished: () => {
                              setSaveInProgress(false);
                              setSaving(false);
                              handleCancel?.();
                            }
                          });
                        }}
                        hideStatus={true}
                        hideDelete={true}
                        hideAdd={true}
                      />
                    ))}
                </Grid>
              </ConfirmWrapper>
            );
          },
          onConfirm: () => performSave(values),
          onCancel: () => {
            setSaveInProgress(false);
          },
          confirmWidth: 100,
          cancelWidth: 100,
          confirmLabel: t('buttonsActions.skip'),
          cancelLabel: t('buttonsActions.cancel')
        });
        return;
      }
    } catch (err) {
      console.error('Error checking for existing tour applications', err);
    }

    return performSave(values);
  };

  const performSave = async (values) => {
    CREATE_APPLICATION({
      userContext,
      locationId: location._id,
      data: {
        applicant: values,
        moveIn: '',
        hasToured: undefined,
        tourDate: undefined,
        locationId: location._id,
        companyId: params.orgId,
        version: 'v2',
        step: 'verify-identity'
      }
    })
      .then((newData) => {
        setSaveInProgress(false);
        if (newData?.application) {
          navigate(getContinueUrl(newData.application));
        }
      })
      .catch((err) => {
        setSaveInProgress(false);
        console.error({ errData: err.data, err });
        toast.error(err.data?.message ? err.data?.message : err.message || err.name);
      });
  };

  const applicantInfoSchema = yup.object().shape({
    firstName: yup
      .string()
      .max(50, `${t('form.validate.long')}`)
      .required(`${t('form.validate.name')}`)
      .matches(NAME_REGEX, `${t('form.validate.wordCharactersOnly')}`),
    lastName: yup
      .string()
      .max(50, `${t('form.validate.long')}`)
      .required(`${t('form.validate.lastName')}`)
      .matches(NAME_REGEX, `${t('form.validate.wordCharactersOnly')}`),
    middleName: yup
      .string()
      .max(50, `${t('form.validate.long')}`)
      .matches(NAME_REGEX, `${t('form.validate.wordCharactersOnly')}`),
    email: yup
      .string()
      .email(`${t('form.validate.invalidEmail')}`)
      .required(`${t('form.validate.email')}`),
    phone: yup
      .array(
        yup.object({
          number: yup
            .string()
            .phone(`${t('form.validate.invalidPhone')}`)
            .required(`${t('form.validate.phone')}`),
          type: yup.string()
        })
      )
      .required(`${t('form.validate.phone')}`)
  });

  const [saveInProgress, setSaveInProgress] = React.useState(false);

  const { values, touched, errors, handleChange, handleBlur, handleSubmit, setFieldValue } =
    useFormik({
      initialValues,
      validationSchema: applicantInfoSchema,
      onSubmit: handleContinue
    });

  return !open ? null : (
    <PageWrapper>
      <PageContainer>
        <Row className="center">
          <Title>{t('yourInfo.title')}</Title>
        </Row>
        <Row spacing={30}>
          <RowGroup columns="1fr 1fr">
            <FormGroup
              label={t('form.legalFirstName')}
              errorText={touched.firstName && errors.firstName}>
              <Input
                name="firstName"
                value={values.firstName}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </FormGroup>
            <FormGroup
              label={t('form.legalMiddleName')}
              errorText={touched.middleName && errors.middleName}>
              <Input
                name="middleName"
                value={values.middleName}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </FormGroup>
          </RowGroup>
          <RowGroup columns="1fr ">
            <FormGroup
              label={t('form.legalLastName')}
              errorText={touched.lastName && errors.lastName}>
              <Input
                name="lastName"
                value={values.lastName}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </FormGroup>
          </RowGroup>
          <FormGroup label={t('form.phone')}>
            <Phone
              errors={errors.phone}
              name="phone"
              value={values.phone}
              onChange={handleChange}
            />
          </FormGroup>
          <FormGroup label={t('form.email')} errorText={touched.email && errors.email}>
            <Input
              icon="fa-regular fa-at"
              type="email"
              name="email"
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </FormGroup>
        </Row>
        <Row className="center">
          <Button
            radius="25px"
            variant="primary"
            disabled={saveInProgress}
            onClick={handleSubmit}
            width={95}>
            {t('buttonsActions.next')}
          </Button>
        </Row>
      </PageContainer>
    </PageWrapper>
  );
};
